import { handleHttpRequest, http, type HttpResponse } from 'interfaces'
import {
  type BaseAddress, type BaseUrl, type Person,
  type Role, formatLeadershipAddress,
  type UsPhone
} from 'services'
import { type Chamber, type State } from 'components'

export interface LeadershipOfficer {
  id: number
  person: Person
  role: Role
  state: State
  legchamber: Chamber
  ofcname: string
  display_order: number
  updatedBy: string
  lastUpdated: string
}

export interface LeadershipOfficerFilterResults {
  data: LeadershipOfficer[]
  page_number: number
  page_size: number
  total_pages: number
  total_items: number
}

export interface LeadershipOfficerWithAddressAndUrls extends LeadershipOfficer {
  address: LeadershipAddress
  urls: LeadershipUrl[]
  responsibilities: number[]
}

export interface LeadershipAddress extends BaseAddress {
  legOfcId: number
  phones: LeadershipPhone[]
}

export interface LeadershipUrl extends BaseUrl {
  legOfcId: number
}

export interface LeadershipPhone extends UsPhone {
  legOfcId: number
}

export interface LeadershipOfficerDisplayOrder {
  id: number
  ofcName: string
  displayOrder: number
  leaderName?: string
}

export async function leadershipOfficerListFilter (params: any): Promise<LeadershipOfficerFilterResults> {
  return await http.get('/api/v1/us/leadership-officers/', {
    params: { ...params }
  }).then(value => value.data)
    .catch(_reason => { return { data: [], page_number: 0, page_size: 0, total_items: 0, total_pages: 0 } })
}

export async function getLeadershipBasicInfo (legOfcId: number): Promise<HttpResponse<LeadershipOfficer>> {
  return await handleHttpRequest(http.get(`/api/v1/us/leadership-officers/${legOfcId}/`))
}

export async function getLeadershipFullInfo (legOfcId: number): Promise<HttpResponse<LeadershipOfficerWithAddressAndUrls>> {
  const response = await handleHttpRequest<LeadershipOfficerWithAddressAndUrls>(http.get(`/api/v1/us/leadership-officers/${legOfcId}/`))
  if (response.result === null) {
    return response
  }
  formatLeadershipAddress(response.result)

  return response
}

export async function getLeadershipDisplayOrder (chamberId: number, stateId?: number): Promise<LeadershipOfficerDisplayOrder[]> {
  const params: Record<string, number> = { chamber_id: chamberId }
  if (stateId != null) {
    params.state_id = stateId
  }

  return await http.get('/api/v1/us/leadership-officers/display-order/', { params })
    .then(response => response.data)
    .catch(() => {
      console.error('Failed to fetch leadership officer display order')
      return []
    })
}

const createIdAndDisplayOrderMap = (officers: LeadershipOfficerDisplayOrder[]): Record<number, number> => {
  const idAndDisplayOrderMap: Record<number, number> = {}

  officers.forEach((officer, index) => {
    idAndDisplayOrderMap[officer.id] = index + 1
  })
  return idAndDisplayOrderMap
}

export async function updateLeadershipOfficerDisplayOrder (
  officers: LeadershipOfficerDisplayOrder[]
): Promise<HttpResponse<LeadershipOfficerDisplayOrder[]>> {
  const idAndDisplayOrderMap = createIdAndDisplayOrderMap(officers)
  const params = {
    idAndDisplayOrderMap
  }

  return await handleHttpRequest(http.patch('/api/v1/us/leadership-officers/display-order/', params))
}
